

























































































































import Vue from 'vue';
import LoadingErrorable from '@/models/util/LoadingErrorable';
import Loader from '@/components/util/Loader.vue';
import SpriteIcon from '@/components/util/SpriteIcon.vue';
import CashShopProvider from '@/api/CashShopProvider';
import ItemsProvider, { CashFilter } from '@/api/ItemsProvider';
import JobProvider, { hiddenClasses } from '@/api/JobProvider';
import IJob from '@/models/jobs/IJob';
import ICashShopEntry, { ICashShopSubTab, ICashShopTab } from '@/models/items/ICashShop';
import { categoryToTabNum, entryMatchesBaseJob } from '@/util/cashShop';
import { hasGrant } from '@/util/Utils';
import CashShopEntryCard from './CashShopEntryCard.vue';
import CashShopEntryDetail from './CashShopEntryDetail.vue';

const GRID_RENDER_CAP = 400;

interface IData {
    tabsData: LoadingErrorable<ICashShopTab[], any>;
    entriesData: LoadingErrorable<ICashShopEntry[], any>;
    activeTab: number;
    activeSubTab: number;
    includeOffSale: boolean;
    searchText: string;
    searchData: LoadingErrorable<ICashShopEntry[], any> | null;
    classFilter: number;
    baseJobs: IJob[];
    parentByJob: { [id: number]: number };
    needJobByItem: { [id: number]: number[] };
}

export default Vue.extend({
    components: {
        Loader,
        SpriteIcon,
        CashShopEntryCard,
        CashShopEntryDetail,
    },
    data(): IData {
        return {
            tabsData: new LoadingErrorable(),
            entriesData: new LoadingErrorable(),
            activeTab: -1,
            activeSubTab: -1,
            includeOffSale: false,
            searchText: '',
            searchData: null,
            classFilter: 0,
            baseJobs: [],
            parentByJob: {},
            needJobByItem: {},
        };
    },
    computed: {
        techMode(): boolean {
            return this.$store.state.techMode;
        },
        searchActive(): boolean {
            return this.searchData !== null;
        },
        visibleTabs(): ICashShopTab[] {
            if (!this.tabsData.value) {
                return [];
            }

            return this.tabsData.value.filter((t) => t.visible || this.techMode);
        },
        currentTab(): ICashShopTab | null {
            return (this.tabsData.value || []).find((t) => t.mtNum === this.activeTab) || null;
        },
        visibleSubTabs(): ICashShopSubTab[] {
            if (!this.currentTab) {
                return [];
            }

            return this.currentTab.subTabs.filter((s) => s.visible || this.techMode);
        },
        detailSn(): number | null {
            const sn = Number(this.$route.params.sn);
            return sn > 0 ? sn : null;
        },
        classFilterReady(): boolean {
            if (!this.classFilter) {
                return true;
            }

            const src = this.searchActive
                ? (this.searchData && this.searchData.value ? this.searchData.value : [])
                : (this.entriesData.value || []);
            return src.every((e) => !e.itemIds.length || this.needJobByItem[e.itemIds[0]] !== undefined);
        },
        filteredEntries(): ICashShopEntry[] {
            return this.applyClassFilter(this.entriesData.value || []);
        },
        visibleEntries(): ICashShopEntry[] {
            const entries = this.filteredEntries;
            return entries.length > GRID_RENDER_CAP ? entries.slice(0, GRID_RENDER_CAP) : entries;
        },
        truncatedFrom(): number {
            const entries = this.filteredEntries;
            return entries.length > GRID_RENDER_CAP ? entries.length : 0;
        },
        filteredSearchEntries(): ICashShopEntry[] {
            return this.applyClassFilter(this.searchData && this.searchData.value ? this.searchData.value : []);
        },
        visibleSearchEntries(): ICashShopEntry[] {
            const entries = this.filteredSearchEntries;
            return entries.length > GRID_RENDER_CAP ? entries.slice(0, GRID_RENDER_CAP) : entries;
        },
        searchTruncatedFrom(): number {
            const entries = this.filteredSearchEntries;
            return entries.length > GRID_RENDER_CAP ? entries.length : 0;
        },
    },
    created() {
        this.loadTabs();
        this.loadJobs();
    },
    watch: {
        includeOffSale() {
            this.loadEntries();
        },
        techMode() {
            //  Falling out of tech mode can leave a hidden tab/sub-tab active.
            if (!this.techMode) {
                this.includeOffSale = false;
                const tab = this.currentTab;
                if (!tab || !tab.visible) {
                    this.selectFirstVisible();
                } else {
                    const sub = tab.subTabs.find((s) => s.stNum === this.activeSubTab);
                    if (!sub || !sub.visible) {
                        this.selectTab(tab.mtNum);
                    }
                }
            }
        },
        '$store.state.regionCode'() {
            this.clearSearch();
            this.classFilter = 0;
            this.needJobByItem = {};
            this.loadJobs();
            this.loadTabs();
        },
    },
    methods: {
        async loadTabs() {
            const loader = new LoadingErrorable<ICashShopTab[], any>();
            this.tabsData = loader;
            this.entriesData = new LoadingErrorable();
            loader.startLoad();
            try {
                const tabs = await CashShopProvider.getTabs();
                if (this.tabsData !== loader) return;
                loader.done(tabs);
                await this.activateForDeepLink(tabs);
            } catch (error) {
                if (this.tabsData !== loader) return;
                loader.failed(error);
            }
        },
        async activateForDeepLink(tabs: ICashShopTab[]) {
            const sn = this.detailSn;
            if (sn !== null) {
                try {
                    const entry = await CashShopProvider.getEntry(sn);
                    if (entry) {
                        const tabNum = categoryToTabNum(entry.category, entry.subCategory, tabs);
                        const tab = tabNum !== null ? tabs.find((t) => t.mtNum === tabNum) : null;
                        if (tab && tab.subTabs.some((s) => s.stNum === entry.subCategory)) {
                            this.activeTab = tab.mtNum;
                            this.activeSubTab = entry.subCategory;
                            this.loadEntries();
                            return;
                        }
                    }
                } catch (e) {
                    //  Fall through to the default tab; the panel shows its own error.
                }
            }

            this.selectFirstVisible();
        },
        selectFirstVisible() {
            const first = this.visibleTabs[0];
            if (first) {
                this.selectTab(first.mtNum);
            }
        },
        selectTab(mtNum: number) {
            this.activeTab = mtNum;
            const tab = this.currentTab;
            const firstSub = tab ? tab.subTabs.filter((s) => s.visible || this.techMode)[0] : null;
            this.activeSubTab = firstSub ? firstSub.stNum : -1;
            this.loadEntries();
        },
        selectSubTab(stNum: number) {
            this.activeSubTab = stNum;
            this.loadEntries();
        },
        async loadEntries() {
            if (this.activeTab < 0 || this.activeSubTab < 0) {
                this.entriesData = new LoadingErrorable();
                return;
            }

            const loader = new LoadingErrorable<ICashShopEntry[], any>();
            this.entriesData = loader;
            loader.startLoad();
            try {
                const entries = await CashShopProvider.getEntries(this.activeTab, this.activeSubTab, this.includeOffSale);
                loader.done(entries);
                this.ensureJobData();
            } catch (error) {
                loader.failed(error);
            }
        },
        async loadJobs() {
            try {
                let all = await JobProvider.getAllJobs();
                if (!hasGrant('ref.pdev.duck')) {
                    all = all.filter((j) => hiddenClasses.indexOf(j.id) === -1);
                }

                this.baseJobs = all.filter((j) => j.jobNumber === 0 && j.id > 0);
                const map: { [id: number]: number } = {};
                for (const j of all) {
                    map[j.id] = j.parentJobId;
                }
                this.parentByJob = map;
            } catch (e) {
                //  No job data -> the filter row simply doesn't render.
                this.baseJobs = [];
            }
        },
        setClassFilter(jobId: number) {
            this.classFilter = this.classFilter === jobId ? 0 : jobId;
            this.ensureJobData();
        },
        applyClassFilter(entries: ICashShopEntry[]): ICashShopEntry[] {
            if (!this.classFilter || !this.classFilterReady) {
                return entries;
            }

            return entries.filter((e) => {
                const need = e.itemIds.length ? this.needJobByItem[e.itemIds[0]] : [];
                return entryMatchesBaseJob(need || [], this.classFilter, this.parentByJob);
            });
        },
        async ensureJobData() {
            if (!this.classFilter) {
                return;
            }

            const src = [
                ...(this.entriesData.value || []),
                ...(this.searchData && this.searchData.value ? this.searchData.value : []),
            ];
            const ids = [...new Set(src
                .map((e) => e.itemIds[0])
                .filter((id) => id && this.needJobByItem[id] === undefined))];
            if (!ids.length) {
                return;
            }

            try {
                const fetched = await CashShopProvider.getNeedJobClasses(ids);
                this.needJobByItem = { ...this.needJobByItem, ...fetched };
            } catch (e) {
                //  Fail open: treat unfetchable items as usable-by-all so the
                //  grid never wedges on the filtering loader.
                const fallback: { [id: number]: number[] } = {};
                for (const id of ids) {
                    fallback[id] = [];
                }
                this.needJobByItem = { ...this.needJobByItem, ...fallback };
            }
        },
        openEntry(sn: number) {
            if (String(this.$route.params.sn) !== String(sn)) {
                this.$router.push(`/items/cashshop/${sn}`);
            }
        },
        closeDetail() {
            this.$router.push('/items/cashshop');
        },
        async doSearch() {
            const name = this.searchText.trim();
            if (!name) {
                return;
            }

            const loader = new LoadingErrorable<ICashShopEntry[], any>();
            this.searchData = loader;
            loader.startLoad();
            try {
                const result = await ItemsProvider.search({
                    name,
                    ranks: [],
                    types: [],
                    parts: [],
                    cash: CashFilter.ALL,
                    categories: [],
                    classes: [],
                    minLevel: 0,
                    maxLevel: 999,
                });
                const itemIds = result.items.map((i) => i.id).slice(0, 200);
                let entries = await CashShopProvider.findByItemIds(itemIds);
                if (!this.includeOffSale) {
                    entries = entries.filter((e) => e.onSale);
                }
                loader.done(entries);
                this.ensureJobData();
            } catch (error) {
                loader.failed(error);
            }
        },
        clearSearch() {
            this.searchText = '';
            this.searchData = null;
        },
        originLabel(e: ICashShopEntry): string {
            const tabs = this.tabsData.value || [];
            const tabNum = categoryToTabNum(e.category, e.subCategory, tabs);
            const tab = tabNum !== null ? tabs.find((t) => t.mtNum === tabNum) : null;
            if (!tab) {
                return '';
            }

            const sub = tab.subTabs.find((s) => s.stNum === e.subCategory);
            return sub ? `${tab.name} › ${sub.name}` : tab.name;
        },
    },
});
